import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import {
	AUTO_REFINANCE_PATH,
	BUSINESS_LOANS_DEFAULT_PATH,
	PERSONAL_LOANS_DEFAULT_PATH,
} from "../../../config/site/thecreditreview";
import { NativeSelectDropdownIcon } from "../../insuranceranked/assets/icons/NativeSelectDropdownIcon";
import { getFilteringControls } from "../../selectors";
import { ExpandMobileFiltersIcon } from "../../thecreditreview/assets/icons/ExpandMobileFiltersIcon";
import { MobileFilterSelectIcon } from "../../thecreditreview/assets/icons/MobileFilterSelectIcon";
import { BaseVerticalCheck } from "../BaseComponents/BaseVerticalCheck";
import {
	FilterProvider,
	useDesktopFilterSelect,
	useFilter,
	useMobileFilterSelect,
} from "./FilteringProvider";

export const TheCreditReviewFilter = ({ defaultIsOpen = false }) => (
	<BaseVerticalCheck
		vertical={[
			PERSONAL_LOANS_DEFAULT_PATH,
			AUTO_REFINANCE_PATH,
			BUSINESS_LOANS_DEFAULT_PATH,
		]}
	>
		<FilterProvider defaultIsOpen={defaultIsOpen}>
			<TheCreditReviewFilterContent />
		</FilterProvider>
	</BaseVerticalCheck>
);

TheCreditReviewFilter.propTypes = {
	defaultIsOpen: PropTypes.bool,
};

export const TheCreditReviewFilterContent = () => {
	const filteringControls = useSelector(getFilteringControls);

	return (
		<>
			{/* Mobile View */}
			<div className="tw:fixed tw:right-0 tw:bottom-[15px] tw:left-0 tw:z-[1] tw:flex tw:justify-center tw:sm:hidden">
				<div className="tw:rounded-[28px] tw:bg-[#ff5161] tw:px-[15px]">
					<ExpandMobileFilters />
					<MobileFilters>
						{filteringControls.map((control, idx) => (
							<MobileFilterSelect
								key={`${control.filteringKey}-${idx}`}
								filteringControl={control}
							/>
						))}
					</MobileFilters>
				</div>
			</div>

			{/* Desktop View */}
			<div className="tw:hidden tw:sm:block">
				<div className="tw:relative tw:mt-[-22px] tw:w-full tw:bg-white tw:px-[15px] tw:text-[100%] tw:shadow-[0_4px_4px_0_rgba(0,0,0,0.07)]">
					<div className="tw:relative tw:top-[24px] tw:mx-auto tw:mb-[40px] tw:flex tw:w-full tw:items-center tw:rounded-[5px] tw:bg-white tw:p-2.5 tw:shadow-[2px_1px_20px_#d3d3d3] tw:sm:mb-0 tw:md:top-[30px] tw:md:max-w-[690px] tw:desktop:max-w-[910px] tw:lg2:max-w-[1110px]">
						<div className="tw:flex tw:flex-wrap tw:gap-[15px]">
							{filteringControls.map((control, idx) => (
								<DesktopFilterSelect
									key={`${control.filteringKey}-${idx}`}
									filteringControl={control}
								/>
							))}
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export const MobileFilterSelect = ({ filteringControl }) => {
	const { title, options } = filteringControl;
	const { isOpen, toggle, displayIndicator, handleChange, displayValue } =
		useMobileFilterSelect(filteringControl);

	return (
		<>
			<div
				className="tw:flex tw:h-[40px] tw:w-full tw:cursor-pointer tw:items-center tw:justify-between tw:rounded-[2px] tw:border tw:border-solid tw:border-[rgb(214,219,222)] tw:bg-white tw:px-[15px] tw:py-[13px] tw:text-[#333]"
				onClick={toggle}
				data-testid="select-container"
			>
				<div className="tw:relative tw:inline-flex tw:items-center">
					<p className="tw:my-[4px] tw:block tw:text-left tw:text-[14px] tw:font-semibold">
						{title}
					</p>
					{displayIndicator && (
						<span className="tw:ml-[15px] tw:inline-block tw:h-[7px] tw:w-[7px] tw:rounded-full tw:bg-[#1d8ce0]" />
					)}
				</div>
				<div className="tw:inline-flex tw:items-center">
					{displayValue && (
						<p
							role="button"
							aria-haspopup="true"
							className="tw:m-[4px_0] tw:block tw:text-left tw:text-[14px] tw:font-semibold"
						>
							{displayValue}
						</p>
					)}
					<MobileFilterSelectIcon className="tw:ml-[30px] tw:h-[7px] tw:w-[11px] tw:text-[#606E79]" />
				</div>
			</div>
			{isOpen && (
				<div
					id="optionsContainer"
					role="listbox"
					aria-label={title}
					aria-expanded="true"
					className="tw:fixed tw:bottom-0 tw:left-0 tw:z-1 tw:flex tw:w-full tw:flex-col tw:rounded-t-[10px] tw:bg-white tw:p-[25px] tw:transition-all"
				>
					<p className="tw:my-[4px] tw:block tw:text-center tw:font-avenirNext tw:text-[15px] tw:leading-normal tw:font-semibold tw:text-[#333]">
						{title}
					</p>
					{options.map((option, idx) => (
						<button
							key={`${option.value}-${idx}`}
							role="option"
							value={option.value}
							onClick={handleChange}
							hidden={option.value === "default"}
							className="tw:m-[5px_5px_5px_0] tw:min-w-[75px] tw:rounded-[5px] tw:border-none tw:bg-[#e4e4e4] tw:p-[8px] tw:font-avenirNext tw:text-[13px] tw:leading-[1.4375em] tw:font-semibold tw:text-[#111] tw:hover:bg-[#1d8ce0] tw:hover:text-white"
						>
							{option.label}
						</button>
					))}
				</div>
			)}
		</>
	);
};

MobileFilterSelect.propTypes = {
	filteringControl: PropTypes.shape({
		title: PropTypes.string.isRequired,
		filteringKey: PropTypes.string.isRequired,
		options: PropTypes.arrayOf(
			PropTypes.shape({
				value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
					.isRequired,
				label: PropTypes.string.isRequired,
			}),
		).isRequired,
	}).isRequired,
};

export const DesktopFilterSelect = ({ filteringControl }) => {
	const { title, filteringKey, options, icon } = filteringControl;
	const { selectedOption, handleChange } = useDesktopFilterSelect(filteringKey);

	return (
		<div className="tw:relative tw:flex tw:h-[40px] tw:w-full tw:items-center tw:justify-between tw:rounded-[3px] tw:border tw:border-solid tw:border-[#d6dbde] tw:pt-[5px] tw:pr-[6px] tw:pb-[6px] tw:pl-[48px] tw:md:w-[325px] tw:lg2:w-[350px]">
			<label
				htmlFor={`${filteringKey}_Input`}
				className="tw:mr-[15px] tw:mb-0 tw:font-avenirNextDemi tw:text-[14px] tw:leading-[1.4375em] tw:font-semibold tw:tracking-[0.00938em] tw:whitespace-nowrap tw:text-black"
			>
				<img
					src={icon}
					alt={title}
					className="tw:absolute tw:top-[7px] tw:left-[10px] tw:h-6 tw:w-6"
				/>
				<span className="tw:align-sub">{title}</span>
			</label>
			<div className="tw:relative tw:inline-flex">
				<select
					className="tw:box-content tw:h-[1.4375em] tw:w-full tw:cursor-pointer tw:appearance-none tw:border-none tw:bg-none tw:pt-[4px] tw:pr-[24px] tw:pb-[5px] tw:pl-0 tw:font-avenirNextDemi tw:text-[14px] tw:tracking-[0.00938em] tw:text-black"
					onChange={handleChange}
					value={selectedOption}
					id={`${filteringKey}_Input`}
					data-filtering-key={filteringKey}
				>
					{options.map((option, optionIdx) => (
						<option
							key={`${option.value}-${optionIdx}`}
							value={option.value}
							disabled={option.value === "default"}
						>
							{option.label}
						</option>
					))}
				</select>
				<NativeSelectDropdownIcon className="tw:pointer-events-none tw:absolute tw:top-[calc(50%-0.5em)] tw:right-0 tw:text-2xl tw:text-[#1d8ce0]" />
			</div>
		</div>
	);
};

DesktopFilterSelect.propTypes = {
	filteringControl: PropTypes.shape({
		title: PropTypes.string.isRequired,
		filteringKey: PropTypes.string.isRequired,
		icon: PropTypes.string,
		options: PropTypes.arrayOf(
			PropTypes.shape({
				value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
					.isRequired,
				label: PropTypes.string.isRequired,
			}),
		).isRequired,
	}).isRequired,
};

export const ExpandMobileFilters = () => {
	const { isOpen, toggle, filterCount } = useFilter();
	if (isOpen) return null;

	const filterLabel = filterCount > 1 ? "Filters" : "Filter";

	return (
		<div
			role="button"
			aria-haspopup="true"
			aria-label="Filter Results"
			onClick={toggle}
			className="tw:flex tw:h-[36px] tw:cursor-pointer tw:items-center tw:justify-center"
		>
			<ExpandMobileFiltersIcon className="tw:h-[15px] tw:w-[16px] tw:text-white" />
			<p className="tw:m-0 tw:flex tw:text-[15px] tw:leading-[15px] tw:font-semibold tw:text-white">
				{filterLabel}
				{filterCount > 0 && (
					<span className="tw:mt-[-4px] tw:ml-[6px] tw:rounded-full tw:bg-white tw:px-[7px] tw:pt-[3px] tw:text-[12px] tw:text-[#ff5161]">
						{filterCount}
					</span>
				)}
			</p>
		</div>
	);
};

export const MobileFilters = ({ children }) => {
	const { isOpen, toggle, filterCount, applyFilters, clearFilters } =
		useFilter();
	if (!isOpen) return null;

	return (
		<div className="tw:fixed tw:right-0 tw:bottom-0 tw:left-0 tw:isolate tw:flex tw:transition-all">
			{/* Gray overlay */}
			<div
				className="tw:fixed tw:top-0 tw:left-0 tw:z-1 tw:h-[calc(100vh-50px)] tw:w-full tw:bg-[gray]/30 tw:transition-all"
				onClick={toggle}
			/>

			{/* Filter container */}
			<div className="tw:z-2 tw:flex tw:w-full tw:flex-col tw:rounded-t-[10px] tw:bg-white tw:p-[10px]">
				<p className="tw:my-[10px] tw:inline-flex tw:items-center tw:justify-center tw:text-center tw:text-[16px] tw:font-semibold tw:text-[#111]">
					Filter Results
					{filterCount > 0 && (
						<span className="tw:mt-[-4px] tw:ml-[6px] tw:rounded-full tw:bg-[#ff5161] tw:px-[7px] tw:pt-[3px] tw:text-[12px] tw:text-white">
							{filterCount}
						</span>
					)}
				</p>
				<div className="tw:*:not-first:mt-2.5">{children}</div>
				<div className="tw:flex tw:justify-end">
					<button
						type="button"
						onClick={clearFilters}
						className="tw:mt-[15px] tw:inline-block tw:h-[40px] tw:w-[30%] tw:cursor-pointer tw:touch-manipulation tw:rounded-[3px] tw:border-0 tw:bg-white tw:p-[7px] tw:text-center tw:align-middle tw:font-avenirNextDemi tw:text-[13px] tw:leading-[1.4375em] tw:whitespace-nowrap tw:text-[#111] tw:select-none tw:hover:text-[#1d8ce0] tw:focus:outline-hidden"
					>
						Clear All
					</button>
					<button
						type="button"
						onClick={applyFilters}
						className="tw:mt-[15px] tw:inline-block tw:h-[40px] tw:w-[30%] tw:cursor-pointer tw:touch-manipulation tw:self-end tw:rounded-[3px] tw:border-0 tw:bg-[#1774ff] tw:p-[7px] tw:text-center tw:align-middle tw:font-avenirNextDemi tw:text-[13px] tw:leading-[1.4375em] tw:whitespace-nowrap tw:text-white tw:select-none tw:focus:outline-hidden"
					>
						Apply Filters
					</button>
				</div>
			</div>
		</div>
	);
};

MobileFilters.propTypes = {
	children: PropTypes.node.isRequired,
};
